import { ColumnSource, RowModel, RowSource } from "@omegagrid/grid";
import { ColorTypes } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { NodeSource, TreeAdapter, TreeOptions, TreeSource } from "@omegagrid/tree";
import rp, { GridEditorAdapter, GridEditorOptions, RowData } from "@reporting/rp-app-host";
import { MDM_TABLE_COL_TYPES, MdmColTypes } from "../types";
import { TableDefinition, MdmColumnDefinition } from "@reporting/rp-types/mdm";
import { Thread } from "@reporting/rp-types/etl";
import { KEYED_TREE_OPTIONS } from "./keyedTree";

class MdTreeAdapter extends TreeAdapter {

	private items: NodeSource[];

	constructor(
		private options?: Partial<TreeOptions>
	) { super() }

	async getTree(): Promise<TreeSource> {
		if (this.items) return this.items;
		const tables = await rp.ms('mdm').get<TableDefinition[]>('tableDefinitions', { where: { type: 'MDM' } })
		const createdFolders = new Set<string>();
		const folderItems: NodeSource[] = [];
		const items: NodeSource[] = [];

		for (const table of tables) {
			let nodeLvl = 0;
			let tableName = table.md_name;
			const nameParts = table.md_name.split('/');
			if (nameParts.length > 1) {
				tableName = nameParts.pop();
				nameParts.forEach((folderName, index) => {
					const path = nameParts.slice(0, index + 1).join('/');
					if (!createdFolders.has(path)) {
						createdFolders.add(path);
						folderItems.push({
							k: path,
							v: folderName,
							l: index,
							collapsed: true,
							ch: false,
						});
					}
					nodeLvl++;
				});
			}

			const newItem: NodeSource = {
				k: table.id_md.toString(),
				v: tableName,
				l: nodeLvl,
			};
			if (nodeLvl == 0) items.push(newItem);
			else folderItems.push(newItem);
		}
		return [...folderItems, ...items];
	}

	getOptions(): Partial<TreeOptions> {
		return { ...KEYED_TREE_OPTIONS, ...this.options };
	}

}

class QueryTreeAdapter extends TreeAdapter {

	private items: NodeSource[];

	constructor(
		private options?: Partial<TreeOptions>
	) { super() }

	async getTree(): Promise<TreeSource> {
        if (this.items) return this.items;
        const threads = await rp.ms('etl').get<Thread[]>('threads', {
			where: { type: 'L' },
			order: [['description', 'asc']],
			fields: ['id_thread', 'description'],
			scope: 1 // to include master accounting
		});
		const folderItems: NodeSource[] = [];
		const items: NodeSource[] = [];

		for (const thread of threads) {
			let nodeLvl = 0;
			let threadName = thread.description;
			const nameParts = thread.description.split('/');
			if (nameParts.length > 1) {
				threadName = nameParts.pop();
				nameParts.forEach((folderName, index) => {
					if(folderItems.findIndex(item => item.k == folderName) == -1) {
						const path = nameParts.slice(0, index + 1).join('/');
						folderItems.push({
							k: path,
							v: folderName,
							l: index,
							collapsed: true,
							ch: false,
						});
					}
					nodeLvl++;
				});
			}

			const newItem: NodeSource = {
				k: thread.id_thread.toString(),
				v: threadName,
				l: nodeLvl,
			};
			if (nodeLvl == 0) items.push(newItem);
			else folderItems.push(newItem);
		}
		return [...folderItems, ...items];
    }

	getOptions(): Partial<TreeOptions> {
		return { ...KEYED_TREE_OPTIONS, ...this.options };
	}

}

export class MdmColumnsDefGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];

	constructor (
		private table: TableDefinition
	) { super() }

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			t: 'a',
			e: false,
			w: 5 + 20 * 1,
			content: [{
				action: 'delete',
				title: msg('Delete'),
				icon: 'trash',
				color: ColorTypes.Red
			}],
			filter: false,
			sorting: false,
		}, {
			data: 'property_key',
			header: msg('Column'),
			t: 't',
			e: false,
			w: 105
		}, {
			data: 'property_name',
			header: msg('Name'),
			t: 't',
			w: 250
		}, {
			data: 'property_type',
			header: msg('Type'),
			t: 's',
			w: 120,
			content: MDM_TABLE_COL_TYPES
		}, {
			data: 'property_ro',
			header: msg('Readonly'),
			t: 'b',
			w: 80,

		}, {
			data: 'property_pattern',
			header: msg('Format'),
			t: 't',
			w: 120
		}, {
			data: 'property_id_md', // fixed name - will be reloaded on edit
			header: msg('Dial'),
			t: 's',
			e: false,
			w: 180
		}, {
			data: 'property_ord',
			header: msg('Order'),
			t: 'n',
			z: '0',
			w: 80
		}, {
			data: 'property_opt_sort_num',
			header: msg('Sort priority'),
			t: 'n',
			z: '0',
			w: 115
		}, {
			data: 'property_opt_sort_dir',
			header: msg('Sort direction'),
			t: 's',
			w: 115,
			content: [
				{ k: 'asc', v: msg('Ascending') },
				{ k: 'desc', v: msg('Descending') }
			]
		}, {
			data: 'property',
			t: 'n',
			e: false,
			hidden: true
		}];
		return this._columns;
	}

	async getRows(): Promise<RowSource[]> {
		const columnDefs = await rp.ms('mdm').get<MdmColumnDefinition[]>(`columnDefinitions/${this.table.id_md}`);
		const rows = columnDefs.map(data => ({ data: {
			...data,
			property_type: { v: data.property_type, dependentColumn: 'property_id_md' },
			property_id_md: [MdmColTypes.Dropdown, MdmColTypes.Treelist].includes(data.property_type as MdmColTypes)
				? { e: true, v: data.property_id_md, dv: data.property_name_md, adapter: this.getDialAdapter(data.property_type as MdmColTypes) }
				: null,
			...this.parsePropertyOpt(data.property_opt)
		}}));
		return rows;
	}

	reloadDialAdapters(rows: RowModel[]) {
		const dialColumnIndex = this.editor.grid.model.columns.find(c => c.data === 'property_id_md').index;
		for (const row of rows) {
			const cell = this.editor.grid.model.getCellByCoords(row.index, dialColumnIndex);
			cell.value = null;
			if ([MdmColTypes.Dropdown, MdmColTypes.Treelist].includes(row.data.property_type as MdmColTypes)) {
				cell.editable = true;
				cell.adapter = this.getDialAdapter(row.data.property_type as MdmColTypes);
			} else {
				cell.editable = false;
				cell.adapter = null;
			}
			this.editor.grid.rendering.updateCell(cell);
			// show warning if first or second property is JSON => not work for dropdowns!
			if (row.data.property_type === MdmColTypes.JSON && ([1, 2].includes(row.data.property))) {
				rp.host.bucket.showAlert({
					type: 'warning',
					title: msg('Warning'),
					content: msg('Using JSON type for the first or second property may lead to issues with dropdowns!'),
					timeout: 10000
				});
			}
		}
	}

	private getDialAdapter(type: MdmColTypes): TreeAdapter {
		if (type === MdmColTypes.Dropdown) return new MdTreeAdapter({ selectMode: 'single', autoClose: true });
		if (type === MdmColTypes.Treelist) return new QueryTreeAdapter({ selectMode: 'single', autoClose: true });
		return null;
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			refreshAfterSave: true,
			filters: true,
			saveMode: 'all_rows',
			editable: true,
			pageSize: null,
			toolbar: {
				refresh: {enabled: false},
				new: {enabled: false},
			}
		});
	}

	async save(rowData: RowData[]) {
		for (const row of rowData) {
			const prop = row.data["property"];
			const data = row.state == 'deleted' ? {} : row.data;
			const valid = Boolean(data["property_name"]);
			Object.assign(this.table as any, {
				[`property_name_${prop}`]: data["property_name"] ?? null,
				[`property_type_${prop}`]: data["property_type"] ?? (valid ? MDM_TABLE_COL_TYPES[0].k : null),
				[`property_ro_${prop}`]: data["property_ro"] ?? (valid ? false : null),
				[`property_pattern_${prop}`]: data["property_pattern"] ?? null,
				[`property_ord_${prop}`]: (data["property_ord"] ?? "").toString().length === 0 ? null : (data["property_ord"] ?? null),
				[`property_id_md_${prop}`]: data["property_id_md"] ?? null,
				[`property_opt_${prop}`]: valid ? this.stringfyPropertyOpt(data, { sort_dir: 'asc' }) : null,
			});
		}
		await rp.ms('mdm').post(`tableDefinitions`, this.table);
	}

	onAction(action: string): void {
		if (action === 'discard') this.editor.refresh(); // reload adapters for dial column
	}

	parsePropertyOpt(property_opt: string): Record<string, any> {
		try {
			const options = JSON.parse(property_opt || '{}');
			if (options && typeof options === 'object') {
				return Object.fromEntries(
					Object.entries(options).map(([key, value]) => [`property_opt_${key}`, value])
				);
			}
		} catch (e) {
			console.error('Error parsing property_opt:', e);
		}
		return {};
	}

	stringfyPropertyOpt(data: Record<string, unknown>, defaults: Record<string, unknown> = null): string {
		const options = Object.entries(data)
			.filter(([key]) => key.startsWith('property_opt_'))
			.reduce((acc, [key, value]) => {
				const optionKey = key.replace('property_opt_', '');
				acc[optionKey] = value ?? (defaults && defaults[optionKey] !== undefined ? defaults[optionKey] : null);
				return acc;
			}, {} as Record<string, unknown>);
		return JSON.stringify(options);
	}
}
